import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';
import Tip from '../components/Tip';

interface Position {
  id: string;
  symbol: string;
  entry_price: number;
  current_price: number;
  pnl_pct: number;
  pnl_usd: number;
  stop_price: number;
  size_usd: number;
  hold_days: number;
  remaining_fraction: number;
  pyramided: boolean;
}

interface BotEvent {
  id: string;
  event_type: string;
  message: string;
  metadata: any;
  created_at: string;
}

interface EquitySnapshot {
  equity: number;
  cash: number;
  positions_value: number;
  positions_count: number;
  bull_filter_status: string;
  btc_price: number;
  created_at: string;
}

const STRATEGY_LABELS: Record<string, string> = {
  all: 'All Strategies',
  donchian: 'Donchian',
  ualgo: 'UAlgo',
};

const Dashboard: React.FC = () => {
  const { strategy } = useStrategy();
  const [viewFilter, setViewFilter] = useState<string>('current');
  const [positions, setPositions] = useState<Position[]>([]);
  const [events, setEvents] = useState<BotEvent[]>([]);
  const [equity, setEquity] = useState<EquitySnapshot[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [botStatus, setBotStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [taxReserve, setTaxReserve] = useState<any>(null);
  const [taxPayments, setTaxPayments] = useState<any[]>([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);

  // 'current' uses sidebar strategy, 'all' shows combined
  const effectiveStrategy = viewFilter === 'all' ? undefined : strategy;

  const fetchData = async () => {
    try {
      const [pos, evt, eq, cfg, status, tax] = await Promise.all([
        api.getPositions(effectiveStrategy).catch(() => []),
        api.getEvents(20, effectiveStrategy).catch(() => []),
        api.getEquity(30, effectiveStrategy).catch(() => []),
        api.getConfig(strategy).catch(() => null),
        api.getBotStatus(strategy).catch(() => null),
        api.getTaxReserve().catch(() => null),
      ]);
      setPositions(pos);
      setEvents(evt);
      setEquity(eq);
      setConfig(cfg);
      setBotStatus(status);
      if (tax) {
        setTaxReserve(tax);
        if (tax.enabled) {
          api.getTaxPayments().then(setTaxPayments).catch(() => {});
        }
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [strategy, viewFilter]);

  // Computed values
  const latestEquity = equity.length > 0 ? equity[equity.length - 1] : null;
  const portfolioValue = latestEquity ? latestEquity.equity : config?.starting_capital || 10000;
  const startingCapital = config?.starting_capital || 10000;
  const totalReturn = ((portfolioValue - startingCapital) / startingCapital * 100);
  const totalReturnUsd = portfolioValue - startingCapital;
  const cash = latestEquity ? latestEquity.cash : startingCapital;
  const posCount = positions.length;
  const maxPos = config?.max_positions || 4;
  const bullStatus = latestEquity?.bull_filter_status || 'BEAR';
  const btcPrice = latestEquity?.btc_price;
  const isBull = bullStatus === 'BULL';
  const bullFilterOn = config?.bull_filter_enabled !== false;
  const shortEnabled = config?.short_enabled !== false;
  const bearFilterOn = config?.bear_filter_enabled !== false;
  const isBearActive = botStatus?.bear_filter_status === 'DEATH_CROSS';

  // Today's P&L (sum of open position P&L)
  const todaysPnl = positions.reduce((sum, p) => sum + (p.pnl_usd || 0), 0);

  // Equity curve SVG
  const buildEquitySvg = () => {
    if (equity.length < 2) {
      return { line: 'M0,120 L600,120', area: 'M0,120 L600,120 L600,240 L0,240 Z', label: `$${startingCapital.toLocaleString()}` };
    }
    const vals = equity.map(e => e.equity);
    const min = Math.min(...vals) * 0.98;
    const max = Math.max(...vals) * 1.02;
    const range = max - min || 1;
    const points = vals.map((v, i) => {
      const x = (i / (vals.length - 1)) * 600;
      const y = 240 - ((v - min) / range) * 220;
      return `${x},${y}`;
    });
    const line = 'M' + points.join(' L');
    const area = line + ` L600,240 L0,240 Z`;
    return { line, area, label: `$${Math.round(max).toLocaleString()}` };
  };
  const svg = buildEquitySvg();

  const timeSince = lastUpdated ? `${Math.round((Date.now() - lastUpdated.getTime()) / 1000)} sec ago` : '...';

  const feedIcon = (type: string) => {
    if (type === 'trade') return { cls: 'feed-buy', icon: '$' };
    if (type === 'error') return { cls: 'feed-sell', icon: '!' };
    return { cls: 'feed-info', icon: 'i' };
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Dashboard <span>Last updated {timeSince}</span></div>
        <div className="header-actions">
          <div className="filter-pills" style={{ display: 'flex', gap: 4 }}>
            {['current', 'all'].map(f => (
              <button
                key={f}
                className={`btn ${viewFilter === f ? 'btn-primary' : ''}`}
                style={{ fontSize: 12, padding: '4px 12px' }}
                onClick={() => setViewFilter(f)}
              >
                {f === 'current' ? (strategy === 'donchian' ? 'Donchian' : 'UAlgo') : 'Combined'}
              </button>
            ))}
          </div>
          <button className="btn" onClick={fetchData}>&#8635; Refresh</button>
          <button className={`btn ${botStatus?.status === 'running' ? 'btn-primary' : ''}`}>
            {botStatus?.status === 'running' ? 'Bot Running' : 'Bot Stopped'}
          </button>
        </div>
      </div>

      {/* Market Status Banner */}
      <div className={`filter-banner ${(!bullFilterOn || isBull) ? 'bull' : 'bear'}`}>
        <div className={`filter-dot ${(!bullFilterOn || isBull) ? 'bull' : 'bear'}`} />
        <div>
          <strong style={{ color: isBull ? '#22c55e' : '#ef4444' }}>
            {isBull ? 'BULL MARKET' : 'BEAR MARKET'}
          </strong>
          {btcPrice && ` — BTC $${btcPrice.toLocaleString()}`}
          {'. '}
          {!bullFilterOn ? (
            <span>Bull filter is <strong style={{ color: '#eab308' }}>disabled</strong> — long entries are open in all conditions.</span>
          ) : isBull ? (
            <span>Long entries are <strong style={{ color: '#22c55e' }}>open</strong> — BTC is above the 200-day SMA.</span>
          ) : (
            <span>Long entries are <strong style={{ color: '#ef4444' }}>paused</strong> — waiting for BTC to reclaim the 200-day SMA.</span>
          )}
          {shortEnabled && bearFilterOn ? (
            isBearActive ? (
              <span>{' '}Short entries are <strong style={{ color: '#22c55e' }}>active</strong> — death cross confirmed.</span>
            ) : (
              <span>{' '}Short entries are <strong style={{ color: '#a1a1aa' }}>waiting</strong> — no death cross.</span>
            )
          ) : shortEnabled && !bearFilterOn ? (
            <span>{' '}Short entries are <strong style={{ color: '#22c55e' }}>open</strong> — bear filter disabled.</span>
          ) : null}
          {' '}Existing positions are unaffected.
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Portfolio Value <Tip text="Total value of all open positions plus available cash" /></div>
          <div className="stat-value">${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub gray">{posCount > 0 ? `${posCount} position${posCount > 1 ? 's' : ''} open` : 'Starting capital'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Return <Tip text="Cumulative profit/loss from all closed trades since inception" /></div>
          <div className={`stat-value ${totalReturn > 0 ? 'green' : totalReturn < 0 ? 'red' : 'gray'}`}>
            {totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(1)}%
          </div>
          <div className={`stat-sub ${totalReturn >= 0 ? 'green' : 'red'}`}>
            {totalReturnUsd >= 0 ? '+' : ''}${totalReturnUsd.toFixed(2)}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open Positions <Tip text="Active trades across all modes (spot, futures long, futures short). Max 4 concurrent." /></div>
          <div className="stat-value">
            {posCount} <span style={{ fontSize: 14, color: '#71717a' }}>/ {maxPos}</span>
          </div>
          <div className="stat-sub gray">Max {maxPos} concurrent</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Cash Available <Tip text="Unallocated capital available for new positions" /></div>
          <div className="stat-value">${cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub gray">{Math.round(cash / portfolioValue * 100)}% cash</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Today's P&amp;L <Tip text="Unrealized profit/loss on currently open positions" /></div>
          <div className={`stat-value ${todaysPnl > 0 ? 'green' : todaysPnl < 0 ? 'red' : 'gray'}`}>
            {todaysPnl >= 0 ? '+' : ''}${todaysPnl.toFixed(2)}
          </div>
          <div className="stat-sub gray">{posCount > 0 ? 'Unrealized' : 'No active trades'}</div>
        </div>
        {taxReserve?.enabled && (
          <div className="stat-card">
            <div className="stat-label">Tax Reserve <Tip text="Estimated tax liability set aside from realized gains" /></div>
            <div className="stat-value" style={{ color: '#eab308' }}>
              ${(taxReserve.current_reserve || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div className="stat-sub gray">
              {taxReserve.total_rate?.toFixed(1)}% of ${(taxReserve.net_gains || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} net gains
            </div>
          </div>
        )}
      </div>

      <div className="grid-2">
        {/* Equity Curve */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              Equity Curve <span className="badge badge-paper">Paper</span>
            </div>
          </div>
          <div className="card-body" style={{ padding: '0 20px 20px' }}>
            <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 600 240" preserveAspectRatio="none" style={{ width: '100%', height: 240 }}>
                <defs>
                  <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <path d="M0,120 L600,120" stroke="#2a2d35" strokeWidth="1" fill="none" strokeDasharray="4" />
                <text x="590" y="118" fill="#71717a" fontSize="10" textAnchor="end">{svg.label}</text>
                <path d={svg.line} stroke="#22c55e" strokeWidth="2" fill="none" />
                <path d={svg.area} fill="url(#eqGrad)" />
              </svg>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Activity</div>
          </div>
          <div className="card-body" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {events.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-text">No activity yet</div>
                <div className="empty-state-sub">Bot events will appear here once the bot starts reporting.</div>
              </div>
            ) : (
              events.map((evt) => {
                const fi = feedIcon(evt.event_type);
                return (
                  <div key={evt.id} className="feed-item">
                    <div className={`feed-icon ${fi.cls}`}>{fi.icon}</div>
                    <div>
                      <div className="feed-text">
                        <span className="badge badge-paper">PAPER</span> {evt.message}
                      </div>
                      <div className="feed-time">{new Date(evt.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Tax Reserve Detail */}
      {taxReserve?.enabled && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Tax Reserve Summary — {taxReserve.tax_year} Tax Year</div>
          </div>
          <div className="card-body">
            <div className="grid-2col" style={{ gap: 24 }}>
              {/* Left: Breakdown */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Realized Gains (wins)</span>
                  <span className="green">+${(taxReserve.total_wins || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Realized Losses</span>
                  <span className="red">-${Math.abs(taxReserve.total_losses || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ borderTop: '1px solid #27272a', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span style={{ color: '#e4e4e7' }}>Net Realized Gains</span>
                  <span style={{ color: '#e4e4e7' }}>${(taxReserve.net_gains || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ height: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Estimated Tax ({taxReserve.total_rate?.toFixed(1)}%)</span>
                  <span style={{ color: '#eab308' }}>${(taxReserve.estimated_tax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Tax Payments Recorded</span>
                  <span style={{ color: '#22c55e' }}>-${(taxReserve.total_payments || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ borderTop: '1px solid #27272a', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span style={{ color: '#eab308' }}>Current Reserve</span>
                  <span style={{ color: '#eab308' }}>${(taxReserve.current_reserve || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ height: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Portfolio Equity</span>
                  <span style={{ color: '#e4e4e7' }}>${(taxReserve.equity || portfolioValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Less Tax Reserve</span>
                  <span className="red">-${(taxReserve.current_reserve || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div style={{ borderTop: '1px solid #27272a', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                  <span style={{ color: '#22c55e' }}>Available Trading Capital</span>
                  <span style={{ color: '#22c55e' }}>${(taxReserve.available_capital || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Right: Payments */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setShowPaymentForm(!showPaymentForm)}>
                    {showPaymentForm ? 'Cancel' : 'Record Tax Payment'}
                  </button>
                </div>

                {showPaymentForm && (
                  <div style={{
                    background: '#1a1c22', borderRadius: 8, padding: 16,
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}>
                    <div style={{ fontSize: 13, color: '#e4e4e7', fontWeight: 600 }}>Record Tax Payment</div>
                    <div>
                      <div style={{ fontSize: 12, color: '#71717a', marginBottom: 4 }}>Amount ($)</div>
                      <input
                        type="number"
                        className="form-input"
                        style={{ width: '100%' }}
                        placeholder="0.00"
                        value={paymentAmount}
                        onChange={e => setPaymentAmount(e.target.value)}
                        min={0}
                        step={0.01}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#71717a', marginBottom: 4 }}>Note (optional)</div>
                      <input
                        type="text"
                        className="form-input"
                        style={{ width: '100%' }}
                        placeholder="e.g. Quarterly estimated tax Q1"
                        value={paymentNote}
                        onChange={e => setPaymentNote(e.target.value)}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: '#71717a' }}>
                      This reduces your tax reserve by the amount you withdrew to pay taxes.
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: 12, alignSelf: 'flex-start' }}
                      disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || paymentSaving}
                      onClick={async () => {
                        setPaymentSaving(true);
                        try {
                          await api.recordTaxPayment(parseFloat(paymentAmount), paymentNote);
                          setPaymentAmount('');
                          setPaymentNote('');
                          setShowPaymentForm(false);
                          // Refresh tax data
                          const [tax, payments] = await Promise.all([
                            api.getTaxReserve().catch(() => null),
                            api.getTaxPayments().catch(() => []),
                          ]);
                          if (tax) setTaxReserve(tax);
                          setTaxPayments(payments);
                        } catch {}
                        setPaymentSaving(false);
                      }}
                    >
                      {paymentSaving ? 'Saving...' : 'Record Payment'}
                    </button>
                  </div>
                )}

                {/* Payment History */}
                <div style={{ fontSize: 13, color: '#a1a1aa', fontWeight: 600, marginTop: 4 }}>Payment History</div>
                {taxPayments.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#71717a', fontStyle: 'italic' }}>No payments recorded yet</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                    {taxPayments.map((p: any) => (
                      <div key={p.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: '#1a1c22', borderRadius: 6, padding: '8px 12px', fontSize: 12,
                      }}>
                        <div>
                          <span style={{ color: '#22c55e', fontWeight: 600 }}>${p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          {p.note && <span style={{ color: '#71717a', marginLeft: 8 }}>{p.note}</span>}
                          <div style={{ color: '#71717a', fontSize: 11, marginTop: 2 }}>
                            {new Date(p.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await api.deleteTaxPayment(p.id);
                              const [tax, payments] = await Promise.all([
                                api.getTaxReserve().catch(() => null),
                                api.getTaxPayments().catch(() => []),
                              ]);
                              if (tax) setTaxReserve(tax);
                              setTaxPayments(payments);
                            } catch {}
                          }}
                          style={{
                            background: 'none', border: 'none', color: '#71717a', cursor: 'pointer',
                            fontSize: 16, padding: '0 4px', lineHeight: 1,
                          }}
                          title="Delete payment"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Open Positions */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            Open Positions <span className="badge badge-paper">Paper</span>
          </div>
          <span className="badge badge-gray">{posCount} active</span>
        </div>
        {posCount === 0 ? (
          <div className="card-body">
            <div className="empty-state">
              <div className="empty-state-icon">O</div>
              <div className="empty-state-text">No open positions</div>
              <div className="empty-state-sub">
                {!bullFilterOn
                  ? `Bull filter disabled — waiting for a ${strategy === 'ualgo' ? 'UAlgo trend' : 'Donchian breakout'} signal.`
                  : !isBull
                    ? `Long entries paused — waiting for BTC to reclaim the 200-day SMA.${shortEnabled ? (isBearActive ? ' Short entries are active.' : ' Shorts waiting for death cross.') : ''}`
                    : `Waiting for a ${strategy === 'ualgo' ? 'UAlgo trend' : 'Donchian breakout'} signal to enter a position.`}
              </div>
            </div>
          </div>
        ) : (
          <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Coin</th>
                {viewFilter === 'all' && <th>Strategy</th>}
                <th>Entry</th>
                <th>Current</th>
                <th>P&L</th>
                <th>Size</th>
                <th>Stop</th>
                <th>Hold</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p: any) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.symbol.replace('-USD', '')}</td>
                  {viewFilter === 'all' && <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{(p.strategy || 'donchian').toUpperCase()}</span></td>}
                  <td>${p.entry_price.toLocaleString()}</td>
                  <td>${p.current_price.toLocaleString()}</td>
                  <td>
                    <span style={{ fontWeight: 600 }} className={p.pnl_pct >= 0 ? 'green' : 'red'}>
                      {p.pnl_pct >= 0 ? '+' : ''}{p.pnl_pct}%
                      <br /><span style={{ fontSize: 11 }}>{p.pnl_usd >= 0 ? '+' : ''}${p.pnl_usd}</span>
                    </span>
                  </td>
                  <td>${p.size_usd.toLocaleString()}</td>
                  <td>${p.stop_price?.toLocaleString() || '—'}</td>
                  <td>{p.hold_days}d</td>
                  <td>
                    {p.pyramided && <span className="badge badge-yellow">PYRAMID</span>}
                    {p.remaining_fraction < 1 && <span className="badge badge-green">TP{p.remaining_fraction <= 0.5 ? '2' : '1'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
